import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { SpaceAccountSettingsModel } from "./SpaceAccountSettingsModel";
import {
  CalendarSettings,
  ChatSettings,
  SpaceAccountSettingsModelData,
  SpaceAccountSettingsPatch,
  VoiceSettings,
} from "./types";
import { computed, makeObservable, runInAction } from "mobx";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { liveQuery, Subscription } from "dexie";
import { BaseError } from "@/domains/errors";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { api } from "@/modules/api";
import { waitForLiveQuerySync } from "@/store/sync/utils";
import { Duration } from "luxon";
import { UpdateSpaceAccountSettingsOperation } from "@/store/sync/operations/space-account-settings/UpdateSpaceAccountSettingsOperation";

/**
 * Observable wrapper for voice settings that provides convenient getters.
 */
class VoiceSettingsObservable {
  constructor(private readonly getVoiceSettings: () => VoiceSettings | undefined) {
    makeObservable(this, {
      customVocabulary: computed,
      processingInstructions: computed,
    });
  }

  get customVocabulary(): string[] {
    return this.getVoiceSettings()?.custom_vocabulary ?? [];
  }

  get processingInstructions(): string {
    return this.getVoiceSettings()?.processing_instructions ?? "";
  }
}

/**
 * Observable wrapper for calendar settings that provides convenient getters.
 */
class CalendarSettingsObservable {
  constructor(private readonly getCalendarSettings: () => CalendarSettings | undefined) {
    makeObservable(this, {
      hideCalendarIntegrationShill: computed,
    });
  }

  get hideCalendarIntegrationShill(): boolean {
    return this.getCalendarSettings()?.hide_calendar_integration_shill ?? false;
  }
}

/**
 * Observable wrapper for chat settings that provides convenient getters.
 */
class ChatSettingsObservable {
  constructor(private readonly getChatSettings: () => ChatSettings | undefined) {
    makeObservable(this, {
      instructions: computed,
    });
  }

  get instructions(): string {
    return this.getChatSettings()?.instructions ?? "";
  }
}

export class AppStoreSpaceAccountSettingsStore extends BaseSyncModelStore<
  SpaceAccountSettingsModel,
  SpaceAccountSettingsModelData
> {
  private _voiceSettings: VoiceSettings | undefined;
  private _calendarSettings: CalendarSettings | undefined;
  private _chatSettings: ChatSettings | undefined;
  private liveQuerySubscription: Subscription | undefined;
  private hasInitialized = false;

  public readyState: AsyncResult<boolean> = asyncResultModule.setLoading();

  /** Nested observable for voice settings with convenient getters */
  public readonly voice: VoiceSettingsObservable;

  /** Nested observable for calendar settings with convenient getters */
  public readonly calendar: CalendarSettingsObservable;

  /** Nested observable for chat settings with convenient getters */
  public readonly chat: ChatSettingsObservable;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountSettings, ...injectedDeps });

    this.voice = new VoiceSettingsObservable(() => this._voiceSettings);
    this.calendar = new CalendarSettingsObservable(() => this._calendarSettings);
    this.chat = new ChatSettingsObservable(() => this._chatSettings);

    makeObservable<
      AppStoreSpaceAccountSettingsStore,
      | "_voiceSettings"
      | "_calendarSettings"
      | "_chatSettings"
      | "hasInitialized"
      | "liveQuerySubscription"
      | "waitForDbAndStartLiveQuery"
      | "startLiveQuery"
      | "handleLiveQueryResult"
      | "handleLiveQueryError"
      | "unsubscribeLiveQuery"
    >(this, {
      _voiceSettings: true,
      _calendarSettings: true,
      _chatSettings: true,
      hasInitialized: true,
      readyState: true,
      voice: false,
      calendar: false,
      chat: false,
      liveQuerySubscription: false,
      waitForDbAndStartLiveQuery: false,
      startLiveQuery: false,
      handleLiveQueryResult: false,
      handleLiveQueryError: false,
      unsubscribeLiveQuery: false,
      createSyncModel: false,
      updateSettingsSynchronously: false,
      updateSettingsUsingSyncOperation: false,
      voiceSettings: computed,
      calendarSettings: computed,
      chatSettings: computed,
      // Keep top-level getters for backward compatibility
      customVocabulary: computed,
      processingInstructions: computed,
    });

    this.waitForDbAndStartLiveQuery();
  }

  /**
   * Waits for MemDB to be ready, then starts the liveQuery.
   * We use setInterval because this store is constructed before AppStore's
   * makeAutoObservable() runs, so MobX reactivity isn't set up yet.
   */
  private waitForDbAndStartLiveQuery(): void {
    const waitForDb = setInterval(() => {
      if (this.store.isMemDbReady) {
        clearInterval(waitForDb);
        this.startLiveQuery();
      }
    }, 100);
  }

  private startLiveQuery(): void {
    if (this.liveQuerySubscription) return;

    const startTime = performance.now();
    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] Starting liveQuery for settings",
    });

    try {
      this.liveQuerySubscription = liveQuery(() => this.localTable.limit(1).toArray()).subscribe({
        next: results => this.handleLiveQueryResult(results, startTime),
        error: err => this.handleLiveQueryError(err),
      });
    } catch (error) {
      this.handleLiveQueryError(error as Error);
    }
  }

  private handleLiveQueryResult(
    results: Array<{ model_data: SpaceAccountSettingsModelData }>,
    startTime: number
  ): void {
    const hasSettings = results.length > 0;
    const settingsData = hasSettings ? results[0].model_data.settings : undefined;

    runInAction(() => {
      // Update settings if we have valid data
      if (settingsData) {
        this._voiceSettings = settingsData.voice;
        this._calendarSettings = settingsData.calendar;
        this._chatSettings = settingsData.chat;
      }

      // Handle first-time initialization
      if (!this.hasInitialized) {
        this.hasInitialized = true;
        this.readyState = asyncResultModule.setReady(true);

        const elapsedMs = Math.round(performance.now() - startTime);
        logger.info({
          message: hasSettings
            ? "[AppStoreSpaceAccountSettingsStore] Settings loaded from local storage"
            : "[AppStoreSpaceAccountSettingsStore] No settings found in local storage (new user or not synced yet)",
          info: {
            elapsedMs,
            hasVoiceSettings: !!settingsData?.voice,
            hasCalendarSettings: !!settingsData?.calendar,
            hasChatSettings: !!settingsData?.chat,
          },
        });
      }
    });
  }

  private handleLiveQueryError(error: Error): void {
    this.unsubscribeLiveQuery();

    const err =
      error instanceof BaseError
        ? error
        : new BaseError({
            message: "[AppStoreSpaceAccountSettingsStore] Failed to initialize settings",
            info: { originalError: objectModule.safeErrorAsJson(error) },
          });

    logger.error({
      message: "[AppStoreSpaceAccountSettingsStore] Error in liveQuery",
      info: { err: objectModule.safeErrorAsJson(err) },
    });

    runInAction(() => {
      // Don't block the app from rendering because of settings errors
      this.hasInitialized = true;
      this.readyState = asyncResultModule.setReady(true);
    });
  }

  private unsubscribeLiveQuery(): void {
    if (this.liveQuerySubscription) {
      this.liveQuerySubscription.unsubscribe();
      this.liveQuerySubscription = undefined;
    }
  }

  createSyncModel(data: SyncUpdateValue<SpaceAccountSettingsModelData>): SpaceAccountSettingsModel {
    return new SpaceAccountSettingsModel({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  get voiceSettings(): VoiceSettings | undefined {
    return this._voiceSettings;
  }

  get calendarSettings(): CalendarSettings | undefined {
    return this._calendarSettings;
  }

  get chatSettings(): ChatSettings | undefined {
    return this._chatSettings;
  }

  // Keep top-level getters for backward compatibility
  get customVocabulary(): string[] {
    return this.voice.customVocabulary;
  }

  get processingInstructions(): string {
    return this.voice.processingInstructions;
  }

  /**
   * Updates settings synchronously by:
   * 1. Making an API call to persist the settings
   * 2. Waiting for the exact model_version returned by the API to appear in the local DB
   *
   * @param settingsPatch - The settings patch to apply (same format as the API)
   * @returns The updated voice settings after the sync completes
   * @throws Error if the API call fails or sync times out
   */
  async updateSettingsSynchronously(
    settingsPatch: SpaceAccountSettingsPatch
  ): Promise<VoiceSettings | undefined> {
    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] updateSettingsSynchronously starting",
      info: { settingsPatch: objectModule.safeAsJson(settingsPatch) },
    });

    // Step 1: Make the API call
    const response = await api.patch("/v2/me/settings", {
      body: {
        settings: settingsPatch,
      },
    });

    if (!response.response.ok) {
      logger.error({
        message: "[AppStoreSpaceAccountSettingsStore] API call failed",
        info: { error: objectModule.safeAsJson(response.error) },
      });
      throw new Error("Failed to save settings");
    }

    if (!response.data) {
      logger.error({
        message: "[AppStoreSpaceAccountSettingsStore] API response missing data",
      });
      throw new Error("API response missing data");
    }

    const { model_id: targetModelId, model_version: targetVersion } = response.data;

    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] API call succeeded, waiting for sync",
      info: { targetModelId, targetVersion },
    });

    // Step 2: Wait for the model with the exact ID and version >= targetVersion to appear in the local DB
    await waitForLiveQuerySync({
      syncStore: this.store.sync,
      query: async () => {
        try {
          const record = await this.localTable.get(targetModelId);
          if (!record) return false;
          return record.model_version >= targetVersion;
        } catch (_) {
          return false;
        }
      },
      timeout: Duration.fromObject({ seconds: 10 }),
    });

    logger.info({
      message: "[AppStoreSpaceAccountSettingsStore] Sync completed, settings updated in local DB",
    });

    return this._voiceSettings;
  }

  /**
   * Updates settings using the sync operation system.
   * This provides optimistic updates and queues the operation for background sync.
   *
   * @param settingsPatch - The settings patch to apply
   */
  async updateSettingsUsingSyncOperation(settingsPatch: SpaceAccountSettingsPatch): Promise<void> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;

    await new UpdateSpaceAccountSettingsOperation({
      store: this.store,
      payload: {
        space_account_id: spaceAccountId,
        settings: settingsPatch,
      },
    }).execute();
  }
}
